import { useCallback, useEffect, useState } from 'react';

import { ICredential, TokenExCardTypes, useListUserCreditCards } from '@givebacks/api/core';
import { useSetAtom } from 'jotai';
import { Box, LoadingOverlay, SegmentedControl, Transition } from 'ui';

import { TokenExCardInfoForm } from '../../molecules/forms/TokenExCardInfoForm';
import { ExistingCardGroup } from '../ExistingCreditCard/ExistingCreditCard';
import { credentialFormAtom, existingCredentialAtom } from './atoms';

type OnCredentialChange = (credential?: ICredential | TokenExCardTypes) => void;

type ExistingCardsProps = {
  onCredentialChange: OnCredentialChange;
  setIsMounted(isMounted: boolean): void;
};
const ExistingCards = ({ onCredentialChange, setIsMounted }: ExistingCardsProps) => {
  const { data: credentials } = useListUserCreditCards();

  const setExistingCredential = useSetAtom(existingCredentialAtom);

  const handleValidCredential = useCallback(
    (credentialId?: ICredential['uuid']) => {
      const credential = credentials?.find((credential) => credential.uuid === credentialId);
      setExistingCredential(credential);
      onCredentialChange(credential);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [credentials, setExistingCredential]
  );

  useEffect(() => {
    return () => setExistingCredential(undefined);
  }, [setExistingCredential]);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  return (
    <ExistingCardGroup credentials={credentials ?? []} onCredentialChange={handleValidCredential} />
  );
};

type CardSelectionProps = {
  hasExistingCredentials: boolean;
  onCredentialChange: OnCredentialChange;
};

const CardSelection = ({ hasExistingCredentials, onCredentialChange }: CardSelectionProps) => {
  const [isExistingCredentialMounted, setIExistingCredentialMounted] = useState(false);

  const setCredentialForm = useSetAtom(credentialFormAtom);
  const setExistingCredential = useSetAtom(existingCredentialAtom);

  const [controlValue, setControlValue] = useState<
    'existing_card' | 'new_card' | string | undefined
  >(hasExistingCredentials ? 'existing_card' : 'new_card');

  // Whenever we switch, lets reset the credential forms
  useEffect(() => {
    setCredentialForm(undefined);
    setExistingCredential(undefined);
    onCredentialChange(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlValue, setCredentialForm, setExistingCredential]);

  return (
    <>
      {hasExistingCredentials && (
        <SegmentedControl
          data={[
            { label: 'Card on File', value: 'existing_card' },
            { label: 'New Card', value: 'new_card' },
          ]}
          value={controlValue}
          onChange={setControlValue}
          fullWidth
          variant="light"
        />
      )}

      <Transition
        mounted={controlValue === 'existing_card'}
        transition="fade"
        duration={150}
        timingFunction="ease"
      >
        {(style) => (
          <Box style={style}>
            <ExistingCards
              onCredentialChange={onCredentialChange}
              setIsMounted={setIExistingCredentialMounted}
            />
          </Box>
        )}
      </Transition>

      <Transition
        mounted={controlValue === 'new_card' && isExistingCredentialMounted == false}
        transition="fade"
        duration={150}
        timingFunction="ease"
      >
        {(style) => (
          <Box style={style}>
            <TokenExCardInfoForm onCredentialChange={onCredentialChange} />
          </Box>
        )}
      </Transition>
    </>
  );
};
type CheckoutCardSelectionProps = {
  onCredentialChange: (credential?: ICredential | TokenExCardTypes) => void;
};
const CheckoutCardProvider = ({ onCredentialChange }: CheckoutCardSelectionProps) => {
  const { isInitialLoading, isLoading, isFetching, data: credentials } = useListUserCreditCards();

  if (isInitialLoading || isLoading || isFetching) {
    return (
      <Box h={400}>
        <LoadingOverlay visible={true} />
      </Box>
    );
  }

  return (
    <CardSelection
      hasExistingCredentials={Boolean(credentials?.length)}
      onCredentialChange={onCredentialChange}
    />
  );
};

export { CheckoutCardProvider };
export type { OnCredentialChange };
